import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import VerseReflectionModal from '../../verse-library/components/VerseReflectionModal';
import VerseReflectionsViewModal from './VerseReflectionsViewModal';
import SDACommentaryDisplay from './SDACommentaryDisplay';

import EGWVerseInsights from './EGWVerseInsights';
import CommentariesVerseInsights from './CommentariesVerseInsights';
import TSKDisplay from './TSKDisplay';
import TopicsDisplay from './TopicsDisplay';
import StrongsNumberCard from '../../../components/StrongsNumberCard';
import AddToLibraryModal from './AddToLibraryModal';
import AddLinkModal from './AddLinkModal';
import { bibleService, strongsService} from '../../../services/bibleService';
import { verseInteractionService } from '../../../services/verseInteractionService';
import { reflectionService } from '../../../services/supabaseService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import VerseEngagement from './VerseEngagement';
import { useNavigate } from 'react-router-dom';
import VerseReflectionAccordion from './VerseReflectionAccordion';
import InterlinearDisplay from './InterlinearDisplay';
import { VerseListSkeleton } from '../../../components/ui/SkeletonLoader';
import { useToast } from '../../../components/ToastNotification';
import { getStrongsPairFor, findStrongsCompanion } from './BibleVersionSelector';
import {
  HIGHLIGHT_COLORS,
  getHighlightStyle,
  loadChapterHighlights,
  upsertHighlight,
  removeHighlight,
  countUserHighlights,
} from '../../../services/highlightService';
import { trackEGWVerseAction, trackCommentariesVerseAction, trackTSKVerseAction, trackVerseActionOpen } from '../../../utils/ga';


// ── VerseActionsPanel — extracted from IIFE for Edge/IE compatibility ─────────
const VerseActionsPanel = ({
  verse,
  book,
  chapter,
  version,
  activeVerseTab,
  isInLibrary,
  isHighlightedByUser,
  highlightData,
  isPickerOpen,
  getTextSizePx,
  handleVerseTabSelect,
  handleAddToMemorization,
  handleAddToLibrary,
  handleAddLink,
  handleHighlightColor,
  handleRemoveHighlight,
  setHighlightPickerVerse,
  handleVerseReferenceClick,
  handleTSKVerseReference,
  user,
  navigate,
  showWarning,
  HIGHLIGHT_COLORS: HCOLORS,
}) => {
  const activeTab = activeVerseTab?.[verse?.number] || null;

  const TABS = [
    { id: 'sda', label: 'SDABC', title: 'SDA Bible Commentary', icon: 'Sparkles', color: 'purple' },
    { id: 'egw', label: 'EGW', title: 'Ellen White Comments', icon: 'BookMarked', color: 'amber' },
    { id: 'commentaries', label: 'Commentaries', title: 'Other commentaries', icon: 'BookText', color: 'teal' },
    { id: 'tsk', label: 'TSK', title: 'Treasury of Scripture Knowledge', icon: 'GitBranch', color: 'indigo' },
    { id: 'topics', label: 'Topics', title: "Nave's Topical Bible", icon: 'BookOpen', color: 'emerald' },
    { id: 'interlinear', label: 'Interlinear', title: 'Original Language Analysis', icon: 'Languages', color: 'violet' },
    { id: 'reflect', label: 'Reflect', title: 'Write a reflection', icon: 'BookOpen', color: 'green' },
    { id: 'memorize', label: 'Memory', title: 'Memorization tools', icon: 'Brain', color: 'blue' },
    ...(!isInLibrary ? [{ id: 'library', label: 'Library', title: 'Add verse to library', icon: 'Plus', color: 'rose' }] : []),
    { id: 'addlink', label: 'Link', title: isInLibrary ? 'Link this verse to another verse' : 'Add verse to library first to create links', icon: 'Link', color: 'cyan' },
    { id: 'highlight', label: isHighlightedByUser ? 'Highlight ✓' : 'Highlight', title: isHighlightedByUser ? 'Change or remove highlight' : 'Highlight this verse', icon: 'Highlighter', color: 'yellow' },
  ];

  const colorMap = {
    purple: { tab: 'bg-violet-600 text-white border-violet-600 shadow-md shadow-violet-200/50', inactive: 'text-foreground/70 border-border bg-muted hover:bg-violet-100/20 hover:text-violet-400 hover:border-violet-400/50 dark:hover:bg-violet-900/20', panel: 'border-border bg-muted/50' },
    amber: { tab: 'bg-amber-500 text-white border-amber-500 shadow-md shadow-amber-200/50', inactive: 'text-foreground/70 border-border bg-muted hover:bg-amber-100/20 hover:text-amber-400 hover:border-amber-400/50 dark:hover:bg-amber-900/20', panel: 'border-border bg-muted/50' },
    teal: { tab: 'bg-teal-600 text-white border-teal-600 shadow-md shadow-teal-200/50', inactive: 'text-foreground/70 border-border bg-muted hover:bg-teal-100/20 hover:text-teal-400 hover:border-teal-400/50 dark:hover:bg-teal-900/20', panel: 'border-border bg-muted/50' },
    indigo: { tab: 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-200/50', inactive: 'text-foreground/70 border-border bg-muted hover:bg-indigo-100/20 hover:text-indigo-400 hover:border-indigo-400/50 dark:hover:bg-indigo-900/20', panel: 'border-border bg-muted/50' },
    emerald: { tab: 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-200/50', inactive: 'text-foreground/70 border-border bg-muted hover:bg-emerald-100/20 hover:text-emerald-400 hover:border-emerald-400/50 dark:hover:bg-emerald-900/20', panel: 'border-emerald-200/30 bg-emerald-50/10' },
    violet: { tab: 'bg-purple-600 text-white border-purple-600 shadow-md shadow-purple-200/50', inactive: 'text-foreground/70 border-border bg-muted hover:bg-purple-100/20 hover:text-purple-400 hover:border-purple-400/50 dark:hover:bg-purple-900/20', panel: 'border-border bg-muted/50' },
    green: { tab: 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-200/50', inactive: 'text-foreground/70 border-border bg-muted hover:bg-emerald-100/20 hover:text-emerald-400 hover:border-emerald-400/50 dark:hover:bg-emerald-900/20', panel: 'border-border bg-muted/50' },
    blue: { tab: 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-200/50', inactive: 'text-foreground/70 border-border bg-muted hover:bg-blue-100/20 hover:text-blue-400 hover:border-blue-400/50 dark:hover:bg-blue-900/20', panel: 'border-border bg-muted/50' },
    rose: { tab: 'bg-rose-600 text-white border-rose-600 shadow-md shadow-rose-200/50', inactive: 'text-foreground/70 border-border bg-muted hover:bg-rose-100/20 hover:text-rose-400 hover:border-rose-400/50 dark:hover:bg-rose-900/20', panel: 'border-border bg-muted/50' },
    cyan: { tab: 'bg-cyan-600 text-white border-cyan-600 shadow-md shadow-cyan-200/50', inactive: 'text-foreground/70 border-border bg-muted hover:bg-cyan-100/20 hover:text-cyan-400 hover:border-cyan-400/50 dark:hover:bg-cyan-900/20', panel: 'border-border bg-muted/50' },
    yellow: { tab: 'bg-amber-400 text-amber-900 border-amber-400 shadow-md shadow-amber-200/50', inactive: 'text-foreground/70 border-border bg-muted hover:bg-amber-100/20 hover:text-amber-400 hover:border-amber-400/50 dark:hover:bg-amber-900/20', panel: 'border-amber-200 bg-amber-50/50' },
  };

  const activeTabObj = TABS?.find(function(t) { return t?.id === activeTab; });
  const activePanelClass = activeTabObj ? (colorMap?.[activeTabObj?.color] ? colorMap?.[activeTabObj?.color]?.panel : 'border-border bg-muted/50') : 'border-border bg-muted/50';

  return (
    <div className="mt-3">
      {/* Tab row */}
      <div className="flex flex-wrap gap-1.5 pb-2 border-b border-border/40">
        {TABS?.map(function(tab) {
          var isActive = activeTab === tab?.id;
          var colors = colorMap?.[tab?.color];
          return (
            <button
              key={tab?.id}
              onClick={function() {
                if (tab?.id === 'memorize') { handleAddToMemorization(verse); return; }
                if (tab?.id === 'library') { handleAddToLibrary(verse); return; }
                if (tab?.id === 'addlink') {
                  if (!isInLibrary) { showWarning('Add this verse to your library first before creating links.', 'Library Required'); return; }
                  handleAddLink(verse); return;
                }
                if (tab?.id === 'highlight') {
                  if (!user) { showWarning('Please sign in to highlight verses.', 'Sign In Required'); navigate('/login', { state: { from: '/bible-reader' } }); return; }
                  setHighlightPickerVerse(function(prev) { return prev === verse?.number ? null : verse?.number; }); return;
                }
                handleVerseTabSelect(verse?.number, tab?.id);
              }}
              title={tab?.title}
              className={'flex items-center gap-1 px-2.5 py-1 rounded-full border font-medium tracking-wide transition-all duration-200 ' + (isActive ? colors?.tab : colors?.inactive)}
              style={{ fontSize: 'calc(' + getTextSizePx() + ' * 0.9)', letterSpacing: '0.02em' }}
            >
              <Icon name={tab?.icon} size={11} />
              {tab?.label}
            </button>
          );
        })}
      </div>
      {/* Inline highlight color picker */}
      {isPickerOpen && (
        <div data-highlight-picker className="flex items-center gap-2 flex-wrap mt-2 mb-1 bg-popover border border-border rounded-xl px-3 py-2.5 shadow-lg">
          <span className="text-xs text-muted-foreground font-medium w-full mb-1">Choose highlight color:</span>
          {HCOLORS?.map(function(c) {
            return (
              <button
                key={c?.id}
                onClick={function(e) { e?.stopPropagation(); handleHighlightColor(verse, c?.id); }}
                title={c?.label}
                className={'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border-2 transition-all hover:scale-105 text-xs font-medium ' + (highlightData && highlightData?.color === c?.id ? 'border-foreground shadow-md scale-105' : 'border-transparent hover:border-current')}
                style={{ backgroundColor: c?.bg, outline: '2px solid ' + c?.border, color: c?.text }}
              >
                {highlightData && highlightData?.color === c?.id && <Icon name="Check" size={11} />}
                {c?.label}
              </button>
            );
          })}
          {isHighlightedByUser && (
            <>
              <div className="w-full h-px bg-border my-1" />
              <button
                onClick={function() { handleRemoveHighlight(verse); }}
                title="Remove highlight"
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors"
              >
                <Icon name="X" size={11} />
                Remove highlight
              </button>
            </>
          )}
        </div>
      )}
      {/* Active panel content */}
      {activeTab && (
        <div
          key={activeTab}
          className={'rounded-b-lg rounded-tr-lg border overflow-hidden ' + activePanelClass}
          style={{ animation: 'verseContentReveal 0.25s ease-out both' }}
        >
          {activeTab === 'sda' && (
            <SDACommentaryDisplay book={book} chapter={chapter} verseNumber={verse?.number} verseId={verse?.verse_id} isVisible={true} onToggleVisibility={function() { handleVerseTabSelect(verse?.number, 'sda'); }} onVerseReference={handleVerseReferenceClick} onClose={function() { handleVerseTabSelect(verse?.number, 'sda'); }} textSizePx={getTextSizePx()} />
          )}
          {activeTab === 'egw' && (
            <EGWVerseInsights book={book} chapter={chapter} verse={verse?.number} onClose={function() { handleVerseTabSelect(verse?.number, 'egw'); }} onVerseReference={handleVerseReferenceClick} textSizePx={getTextSizePx()} />
          )}
          {activeTab === 'commentaries' && (
            <CommentariesVerseInsights book={book} chapter={chapter} verse={verse?.number} onClose={function() { handleVerseTabSelect(verse?.number, 'commentaries'); }} onVerseReference={handleVerseReferenceClick} textSizePx={getTextSizePx()} />
          )}
          {activeTab === 'tsk' && (
            <TSKDisplay book={book} chapter={chapter} verse={verse?.number} verseId={verse?.verse_id} isVisible={true} onToggleVisibility={function() { handleVerseTabSelect(verse?.number, 'tsk'); }} onVerseReference={handleTSKVerseReference} textSizePx={getTextSizePx()} />
          )}
          {activeTab === 'topics' && (
            <TopicsDisplay
              book={book}
              chapter={chapter}
              verse={verse?.number}
              isVisible={true}
              onToggleVisibility={function() { handleVerseTabSelect(verse?.number, 'topics'); }}
              onVerseReference={handleTSKVerseReference}
              textSizePx={getTextSizePx()}
              versionShortName={version}
            />
          )}
          {activeTab === 'interlinear' && (
            <InterlinearDisplay book={book} chapter={chapter} verse={verse?.number} onClose={function() { handleVerseTabSelect(verse?.number, 'interlinear'); }} />
          )}
          {activeTab === 'reflect' && (
            <VerseReflectionAccordion
              verse={{ number: verse?.number, reference: verse?.reference || (book?.charAt(0)?.toUpperCase() + book?.slice(1) + ' ' + chapter + ':' + verse?.number), text: verse?.text }}
              book={book}
              chapter={chapter}
              version={version}
              onClose={function() { handleVerseTabSelect(verse?.number, 'reflect'); }}
              onSaved={() => {
                loadVerseInteractions();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};


const BibleTextDisplay = ({
  book,
  chapter,
  version = 'kjv',
  onVerseSelect,
  onAddToLibrary,
  highlightVerseNumber,
  onVerseReferenceClick,
  audioActiveVerse = null,
  onVerseNumberAudioClick = null,
  hasStrongs = false,
  localizedBookName = null,
  toolbarButtons = null,
  className = ''
}) => {
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [highlightedVerses, setHighlightedVerses] = useState(new Set());
  const [showVerseActions, setShowVerseActions] = useState(null);
  const [isReflectionModalOpen, setIsReflectionModalOpen] = useState(false);
  const [selectedVerseForReflection, setSelectedVerseForReflection] = useState(null);
  const [isViewReflectionsModalOpen, setIsViewReflectionsModalOpen] = useState(false);
  const [selectedVerseForViewing, setSelectedVerseForViewing] = useState(null);
  const [visibleCommentaries, setVisibleCommentaries] = useState(new Set());
  const [visibleEGWComments, setVisibleEGWComments] = useState(new Set());
  const [visibleTSK, setVisibleTSK] = useState(new Set());
  const [visibleEGWVerseInsights, setVisibleEGWVerseInsights] = useState(new Set());
  const [visibleCommentariesInsights, setVisibleCommentariesInsights] = useState(new Set());
  const [isAddToLibraryModalOpen, setIsAddToLibraryModalOpen] = useState(false);
  const [selectedVerseForLibrary, setSelectedVerseForLibrary] = useState(null);
  const [selectedVerseForTSK, setSelectedVerseForTSK] = useState(null);
  const [visibleReflectionAccordions, setVisibleReflectionAccordions] = useState(new Set());

  // Add Link modal state
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);
  const [selectedVerseForLink, setSelectedVerseForLink] = useState(null);

  // Active tab per verse: null = all closed, string = active tab name
  const [activeVerseTab, setActiveVerseTab] = useState({});

  // User's verse library for highlighting
  const [userVerseLibrary, setUserVerseLibrary] = useState(new Map());
  const [libraryLoading, setLibraryLoading] = useState(false);

  // Verse interactions for counts and public reflections
  const [verseInteractions, setVerseInteractions] = useState(new Map());
  const [interactionsLoading, setInteractionsLoading] = useState(false);

  // ── Verse Highlights ──────────────────────────────────────────────────────
  // Map<verseNumber, { id: string, color: string }>
  const [verseHighlights, setVerseHighlights] = useState(new Map());
  // Which verse is showing the color-picker popover
  const [highlightPickerVerse, setHighlightPickerVerse] = useState(null);
  // Total highlight count for community toggle badge
  const [totalHighlightCount, setTotalHighlightCount] = useState(0);

  const { user } = useAuth();
  const navigate = useNavigate();
  const { showWarning, showSuccess, showError } = useToast();

  // Strong's Numbers state
  const [strongsDefinitions, setStrongsDefinitions] = useState({});
  const [activeStrongsCard, setActiveStrongsCard] = useState(null);
  const [cardPosition, setCardPosition] = useState({ x: 0, y: 0 });
  
  // Strong's Numbers toggle state - NEW
  const [showStrongsNumbers, setShowStrongsNumbers] = useState(false);

  // Social Engagement toggle state
  const [showSocialEngagement, setShowSocialEngagement] = useState(false);

  // Text size state: 'sm' | 'base' | 'lg' | 'xl'
  const [textSize, setTextSize] = useState(() => {
    return localStorage.getItem('bibleReaderTextSize') || 'base';
  });

  const TEXT_SIZES = [
    { key: 'sm', label: 'S', px: '13px', mobilePx: '14px', btnFontSize: '10px' },
    { key: 'base', label: 'M', px: '15px', mobilePx: '16px', btnFontSize: '12px' },
    { key: 'lg', label: 'L', px: '18px', mobilePx: '19px', btnFontSize: '15px' },
    { key: 'xl', label: 'XL', px: '22px', mobilePx: '23px', btnFontSize: '18px' },
  ];

  const getTextSizePx = () => {
    const size = TEXT_SIZES?.find(s => s?.key === textSize);
    // Use larger size on mobile (screen width < 640px)
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    return isMobile ? (size?.mobilePx || '16px') : (size?.px || '15px');
  };
  const getTextStyle = () => ({ fontSize: getTextSizePx(), lineHeight: '1.85' });

  // Persist text size to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('bibleReaderTextSize', textSize);
  }, [textSize]);

  // Verse engagement data (users who reflected/memorized)
  const [verseEngagementUsers, setVerseEngagementUsers] = useState(new Map());
  const [engagementLoading, setEngagementLoading] = useState(false);

  // Mock current user - in a real app, this would come from authentication context
  const currentUser = 'Current User';

  // Log book prop for debugging
  useEffect(() => {
    console.log('BibleTextDisplay - book prop:', { book, bookType: typeof book, bookLength: book?.length });
  }, [book]);

  // Phase colors - consistent amber/primary tint for ALL phases (readable in both themes)
  const getPhaseColors = (phase) => {
    // All phases use the same amber tint for consistency and readability
    return 'rgba(245, 158, 11, 0.12)'; // amber-500 at 12% opacity - subtle, readable in both themes
  };

  // Get phase label for tooltip
  const getPhaseLabel = (phase) => {
    const labels = {
      0: 'New',
      1: 'Learning',
      2: 'Reviewing',
      3: 'Familiar',
      4: 'Known',
      5: 'Mastered',
      6: 'Expert',
      7: 'Memorized'
    };
    return labels?.[phase] || `Phase ${phase}`;
  };

  // Load user's verse library for highlighting
  const loadUserVerseLibrary = async () => {
    if (!user?.id || !book || !chapter) return;

    try {
      setLibraryLoading(true);

      const { data, error } = await supabase?.from('verses')?.select('id, book, chapter, verse_start, verse_end, current_phase, reference')?.eq('user_id', user?.id)?.eq('book', book)?.eq('chapter', chapter);

      if (error) {
        console.error('Error loading user verse library:', error);
        return;
      }

      // Create a map of verse numbers to phase data
      const libraryMap = new Map();

      data?.forEach((verse) => {
        const startVerse = verse?.verse_start;
        const endVerse = verse?.verse_end || startVerse;

        // For ranges like "Genesis 1:1-3", mark verses 1, 2, and 3
        for (let verseNum = startVerse; verseNum <= endVerse; verseNum++) {
          libraryMap?.set(verseNum, {
            id: verse?.id,
            phase: verse?.current_phase,
            reference: verse?.reference,
            isRange: startVerse !== endVerse,
            rangeStart: startVerse,
            rangeEnd: endVerse
          });
        }
      });

      setUserVerseLibrary(libraryMap);
    } catch (err) {
      console.error('Network error loading verse library:', err);
    } finally {
      setLibraryLoading(false);
    }
  };

  // Load verse interaction data (reflection counts, library counts, public reflections)
  const loadVerseInteractions = async () => {
    if (!book || !chapter) return;

    try {
      setInteractionsLoading(true);

      const { data: interactionsMap, error } = await verseInteractionService?.getChapterInteractions(
        book,
        chapter,
        null // Can pass version ID if needed
      );

      if (error) {
        console.error('Error loading verse interactions:', error);
        return;
      }

      setVerseInteractions(interactionsMap);
    } catch (err) {
      console.error('Network error loading verse interactions:', err);
    } finally {
      setInteractionsLoading(false);
    }
  };

  // Load verse engagement users (avatars)
  const loadVerseEngagementUsers = async () => {
    if (!book || !chapter) return;

    try {
      setEngagementLoading(true);

      const { data, error } = await verseInteractionService?.getVerseEngagementUsers(
        book,
        chapter,
        null // version ID - can be added if needed
      );

      if (error) {
        console.error('Error loading verse engagement users:', error);
        return;
      }

      setVerseEngagementUsers(data);
    } catch (err) {
      console.error('Failed to load verse engagement users:', err);
    } finally {
      setEngagementLoading(false);
    }
  };

  // Parse Strong's numbers from verse text and create interactive elements
  const parseStrongsNumbers = (text) => {
    // Strip paragraph marks before parsing
    const cleanedText = text?.replace(/¶\s*/g, '') || text;
    // Updated regex to match Strong's numbers in curly braces, including those with parentheses
    // Matches: {H1234}, {G5678}, {(H8688)}, {(G1234)}, etc.
    const strongsRegex = /\{(?:\()?([HG]\d+)(?:\))?\}/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = strongsRegex?.exec(cleanedText)) !== null) {
      // Add text before the Strong's number
      if (match?.index > lastIndex) {
        parts?.push({
          type: 'text',
          content: cleanedText?.slice(lastIndex, match?.index)
        });
      }

      // Add the Strong's number as an interactive element
      parts?.push({
        type: 'strongs',
        content: match?.[0], // Full match with braces (for removal)
        number: match?.[1], // Just the H/G number (clean)
        displayText: match?.[1] // Display without braces or parentheses
      });

      lastIndex = match?.index + match?.[0]?.length;
    }

    // Add remaining text after the last Strong's number
    if (lastIndex < cleanedText?.length) {
      parts?.push({
        type: 'text',
        content: cleanedText?.slice(lastIndex)
      });
    }

    return parts?.length > 0 ? parts : [{ type: 'text', content: cleanedText }];
  };

  // Render verse text without Strong's numbers - NEW
  const renderVerseWithoutStrongs = (text) => {
    // Remove Strong's numbers from text, then strip paragraph marks
    const cleanText = text?.replace(/\{(?:\()?[HG]\d+(?:\))?\}/g, '')?.replace(/¶\s*/g, '');
    return formatKJVText(cleanText);
  };

  // Format KJV text with special markup: [] for italics, ‹› for red text
  const formatKJVText = (text) => {
    if (!text) return text;
    // Strip paragraph marks before processing
    const strippedText = text?.replace(/¶\s*/g, '');

    const parts = [];
    let lastIndex = 0;
    
    // Combined regex to match both [] and ‹› patterns
    const markupRegex = /\[([^\]]+)\]|‹([^›]+)›/g;
    let match;
    
    while ((match = markupRegex?.exec(strippedText)) !== null) {
      // Add text before the match
      if (match?.index > lastIndex) {
        parts?.push(
          <span key={`text-${lastIndex}`}>
            {strippedText?.slice(lastIndex, match?.index)}
          </span>
        );
      }
      
      // Add formatted text based on bracket type
      if (match?.[1]) {
        // Text in [] - italicize
        parts?.push(
          <em key={`italic-${match?.index}`} className="italic">
            {match?.[1]}
          </em>
        );
      } else if (match?.[2]) {
        // Text in ‹› - red color
        parts?.push(
          <span key={`red-${match?.index}`} className="text-destructive">
            {match?.[2]}
          </span>
        );
      }
      
      lastIndex = match?.index + match?.[0]?.length;
    }
    
    // Add remaining text
    if (lastIndex < strippedText?.length) {
      parts?.push(
        <span key={`text-${lastIndex}`}>
          {strippedText?.slice(lastIndex)}
        </span>
      );
    }
    
    return parts?.length > 0 ? parts : strippedText;
  };

  // Format KJV text with underline accents on words (used in Strong's lens mode)
  const formatKJVTextWithAccents = (text) => {
    if (!text) return text;
    
    const parts = [];
    let lastIndex = 0;
    
    // Combined regex to match both [] and ‹› patterns
    const markupRegex = /\[([^\]]+)\]|‹([^›]+)›/g;
    let match;
    
    while ((match = markupRegex?.exec(text)) !== null) {
      if (match?.index > lastIndex) {
        parts?.push(
          <span key={`text-${lastIndex}`} className="strongs-word-segment">
            {text?.slice(lastIndex, match?.index)}
          </span>
        );
      }
      
      if (match?.[1]) {
        parts?.push(
          <em key={`italic-${match?.index}`} className="italic strongs-word-segment">
            {match?.[1]}
          </em>
        );
      } else if (match?.[2]) {
        parts?.push(
          <span key={`red-${match?.index}`} className="text-destructive strongs-word-segment">
            {match?.[2]}
          </span>
        );
      }
      
      lastIndex = match?.index + match?.[0]?.length;
    }
    
    if (lastIndex < text?.length) {
      parts?.push(
        <span key={`text-${lastIndex}`} className="strongs-word-segment">
          {text?.slice(lastIndex)}
        </span>
      );
    }
    
    return parts?.length > 0 ? parts : (
      <span className="strongs-word-segment">{text}</span>
    );
  };

  // Handle Strong's number click/hover
  const handleStrongsClick = async (strongsNumber, event) => {
    event?.stopPropagation();

    // Check if we already have this definition
    if (strongsDefinitions?.[strongsNumber]) {
      showStrongsCard(strongsNumber, event);
      return;
    }

    try {
      const { data, error } = await strongsService?.getStrongsDefinition(strongsNumber);

      if (error) {
        console.error('Error fetching Strong\'s definition:', error);
        return;
      }

      if (data) {
        setStrongsDefinitions((prev) => ({
          ...prev,
          [strongsNumber]: data
        }));
        showStrongsCard(strongsNumber, event);
      }
    } catch (err) {
      console.error('Network error fetching Strong\'s definition:', err);
    }
  };

  // Show Strong's card with positioning
  const showStrongsCard = (strongsNumber, event) => {
    const rect = event?.target?.getBoundingClientRect();
    const scrollY = window.scrollY;

    setCardPosition({
      x: rect?.left + rect?.width / 2,
      y: rect?.top + scrollY
    });

    setActiveStrongsCard(strongsNumber);
  };

  // Handle Strong's touch (mobile)
  const handleStrongsTouch = (strongsNumber, event) => {
    event?.preventDefault(); // Prevent text selection
    handleStrongsClick(strongsNumber, event);
  };

  // Close Strong's card
  const closeStrongsCard = () => {
    setActiveStrongsCard(null);
  };

  // Toggle Strong's numbers visibility - NEW
  const handleToggleStrongsNumbers = () => {
    setShowStrongsNumbers(prev => !prev);
  };

  // Toggle Social Engagement visibility - NEW
  const handleToggleSocialEngagement = () => {
    setShowSocialEngagement(prev => !prev);
  };

  // Render verse text with Strong's numbers as inline superscript elements
  const renderVerseWithStrongs = (text) => {
    const parts = parseStrongsNumbers(text);
    const renderedParts = [];

    parts?.forEach((part, index) => {
      if (part?.type === 'strongs') {
        // Check if next part is also a Strong's number for proper spacing
        const nextPart = parts?.[index + 1];
        const isFollowedByStrongs = nextPart?.type === 'strongs';

        renderedParts?.push(
          <sup
            key={`strongs-${index}`}
            className={`text-amber-400 cursor-pointer font-medium text-xs transition-all duration-150 inline-block hover:text-amber-300 hover:scale-125 hover:drop-shadow-[0_0_4px_rgba(251,191,36,0.7)] active:scale-95 ${
              isFollowedByStrongs ? 'mr-1.5' : 'mr-0.5'
            } ml-0.5`}
            style={{ verticalAlign: 'super' }}
            onClick={(e) => handleStrongsClick(part?.number, e)}
            onTouchStart={(e) => handleStrongsTouch(part?.number, e)}
            title={`Strong's ${part?.displayText} - Click for definition`}>
            {part?.displayText}
          </sup>
        );
      } else {
        // Format text content with KJV markup, wrapping each word with underline accent
        const formattedText = formatKJVTextWithAccents(part?.content);
        renderedParts?.push(
          <span key={`text-${index}`}>{formattedText}</span>
        );
      }
    });

    return renderedParts;
  };

  // Cache of all available version short names (lowercase) for dynamic Strong's companion lookup
  const [allVersionShortNames, setAllVersionShortNames] = useState([]);

  // Load available version short names once on mount for dynamic Strong's pairing
  useEffect(() => {
    bibleService?.getBibleVersions()?.then(({ data }) => {
      if (data) {
        setAllVersionShortNames(data?.map(v => v?.short_name?.toLowerCase()?.trim())?.filter(Boolean));
      }
    })?.catch(() => {});
  }, []);

  // Load verses when book, chapter, or version changes
  useEffect(() => {
    const loadVerses = async () => {
      if (!book || !chapter) {
        setVerses([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // ── Option A: Version Pairing ─────────────────────────────────────────
        // When Strong's toggle is ON and the current version has a paired Strongs
        // version, transparently fetch from the Strongs version instead.
        // All memorization/library features still reference the base version.
        const versionLower = version?.toLowerCase()?.trim();
        const pairedStrongsVersion = showStrongsNumbers
          ? (findStrongsCompanion(versionLower, allVersionShortNames) || getStrongsPairFor(versionLower))
          : null;
        const fetchVersion = pairedStrongsVersion || versionLower;

        const { data, error } = await bibleService?.getChapterVerses(book, chapter, fetchVersion);

        if (error) {
          // If paired Strongs version fetch fails, fall back to base version
          if (pairedStrongsVersion) {
            console.warn(`Strongs version "${pairedStrongsVersion}" not found, falling back to "${versionLower}"`);
            const fallback = await bibleService?.getChapterVerses(book, chapter, versionLower);
            if (fallback?.data && fallback?.data?.length > 0) {
              const transformedVerses = fallback?.data?.map((verse) => ({
                number: verse?.verse_number,
                text: verse?.text,
                reference: verse?.reference,
                verse_id: verse?.verse_id,
                version_id: verse?.version_id,
                version_name: verse?.version_name
              }));
              setVerses(transformedVerses);
              return;
            }
          }
          setError(`Failed to load ${book} ${chapter} (${version?.toUpperCase()}): ${error?.message || 'Unknown error'}`);
          setVerses([]);
        } else if (!data || data?.length === 0) {
          setError(`No verses found for ${book} ${chapter} in ${version?.toUpperCase()}. Please try a different version or chapter.`);
          setVerses([]);
        } else {
          // Transform database verses to match expected format
          const transformedVerses = data?.map((verse) => ({
            number: verse?.verse_number,
            text: verse?.text,
            reference: verse?.reference,
            verse_id: verse?.verse_id,
            version_id: verse?.version_id,
            version_name: verse?.version_name
          }));

          setVerses(transformedVerses);
          console.log(`Successfully loaded ${transformedVerses?.length} verses from ${transformedVerses?.[0]?.version_name} for ${book} ${chapter}${pairedStrongsVersion ? ' (Strongs lens)' : ''}`);
        }
      } catch (err) {
        console.error('Error loading verses:', err);
        setError('Network error. Please check your connection and try again.');
        setVerses([]);
      } finally {
        setLoading(false);
      }
    };

    loadVerses();
  }, [book, chapter, version, showStrongsNumbers]);

  // Load user verse library when chapter changes or user changes
  useEffect(() => {
    loadUserVerseLibrary();
  }, [book, chapter, user?.id]);

  // Load verse interactions when chapter changes
  useEffect(() => {
    loadVerseInteractions();
  }, [book, chapter]);

  // Load verse engagement users when chapter changes
  useEffect(() => {
    loadVerseEngagementUsers();
  }, [book, chapter]);

  // Load chapter highlights from Supabase
  const loadHighlights = async () => {
    if (!user?.id || !book || !chapter) return;
    const { data } = await loadChapterHighlights(user?.id, book, chapter);
    setVerseHighlights(data);
  };

  // Refresh total highlight count
  const refreshHighlightCount = async () => {
    if (!user?.id) return;
    const { count } = await countUserHighlights(user?.id);
    setTotalHighlightCount(count);
  };

  // Handle clicking the highlight swatch in the color picker
  const handleHighlightColor = async (verse, colorId) => {
    if (!user) {
      showWarning('Please sign in to highlight verses.', 'Sign In Required');
      navigate('/login', { state: { from: '/bible-reader' } });
      return;
    }
    setHighlightPickerVerse(null);
    const { data, error } = await upsertHighlight(user?.id, book, chapter, verse?.number, colorId);
    if (!error && data) {
      setVerseHighlights(prev => {
        const next = new Map(prev);
        next?.set(verse?.number, { id: data?.id, color: data?.color });
        return next;
      });
      refreshHighlightCount();
    }
  };

  // Handle removing a highlight
  const handleRemoveHighlight = async (verse) => {
    const hl = verseHighlights?.get(verse?.number);
    if (!hl) return;
    setHighlightPickerVerse(null);
    const { error } = await removeHighlight(hl?.id);
    if (!error) {
      setVerseHighlights(prev => {
        const next = new Map(prev);
        next?.delete(verse?.number);
        return next;
      });
      refreshHighlightCount();
    }
  };

  // Load highlights when chapter/user changes
  useEffect(() => {
    loadHighlights();
  }, [book, chapter, user?.id]);

  // Load total highlight count on mount / user change
  useEffect(() => {
    refreshHighlightCount();
  }, [user?.id]);

  // Close color picker when clicking outside
  useEffect(() => {
    if (!highlightPickerVerse) return;
    const handler = (e) => {
      if (!e?.target?.closest('[data-highlight-picker]')) {
        setHighlightPickerVerse(null);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [highlightPickerVerse]);

  // Clear selections when chapter changes
  useEffect(() => {
    setSelectedVerse(null);
    setHighlightedVerses(new Set());
    setShowVerseActions(null);
    setActiveStrongsCard(null); // Clear Strong's card
    setStrongsDefinitions({}); // Clear cached definitions
    setActiveVerseTab({});
    setHighlightPickerVerse(null);
  }, [book, chapter]);

  const handleVerseClick = (verse) => {
    if (selectedVerse?.number === verse?.number) {
      // Deselect if clicking the same verse — also close actions
      setSelectedVerse(null);
      setShowVerseActions(null);
      // Notify parent to clear EGW insights
      if (onVerseSelect) {
        onVerseSelect(null, null);
      }
    } else {
      setSelectedVerse(verse);
      // Show the action tabs when clicking verse text
      setShowVerseActions(verse?.number);
      // Notify parent with verse reference and number for EGW insights
      if (onVerseSelect) {
        const reference = verse?.reference || `${book?.charAt(0)?.toUpperCase() + book?.slice(1)} ${chapter}:${verse?.number}`;
        onVerseSelect(reference, verse?.number);
      }
    }
  };

  // Toggle verse actions panel (study tools tabs) via verse number button click
  const handleVerseNumberClick = (verse) => {
    // Verse number click only selects/deselects the verse, does NOT toggle action tabs
    if (selectedVerse?.number === verse?.number) {
      setSelectedVerse(null);
      setShowVerseActions(null);
      if (onVerseSelect) {
        onVerseSelect(null, null);
      }
    } else {
      setSelectedVerse(verse);
      if (onVerseSelect) {
        const reference = verse?.reference || `${book?.charAt(0)?.toUpperCase() + book?.slice(1)} ${chapter}:${verse?.number}`;
        onVerseSelect(reference, verse?.number);
      }
    }
  };

  const handleToggleCommentary = (verseNumber) => {
    const newVisible = new Set(visibleCommentaries);
    if (newVisible?.has(verseNumber)) {
      newVisible?.delete(verseNumber);
    } else {
      newVisible?.add(verseNumber);
    }
    setVisibleCommentaries(newVisible);
  };

  const handleToggleEGWComments = (verseNumber) => {
    const newVisible = new Set(visibleEGWComments);
    if (newVisible?.has(verseNumber)) {
      newVisible?.delete(verseNumber);
    } else {
      newVisible?.add(verseNumber);
    }
    setVisibleEGWComments(newVisible);
  };

  // Toggle TSK visibility for a specific verse
  const handleToggleTSK = (verseNumber) => {
    setVisibleTSK((prev) => {
      const newSet = new Set(prev);
      if (newSet?.has(verseNumber)) {
        newSet?.delete(verseNumber);
      } else {
        newSet?.add(verseNumber);
      }
      return newSet;
    });
  };

  const handleToggleEGWVerseInsights = (verseNumber) => {
    setVisibleEGWVerseInsights((prev) => {
      const newSet = new Set(prev);
      if (newSet?.has(verseNumber)) {
        newSet?.delete(verseNumber);
      } else {
        newSet?.add(verseNumber);
      }
      return newSet;
    });
  };

  const handleToggleCommentariesInsights = (verseNumber) => {
    setVisibleCommentariesInsights((prev) => {
      const newSet = new Set(prev);
      if (newSet?.has(verseNumber)) {
        newSet?.delete(verseNumber);
      } else {
        newSet?.add(verseNumber);
      }
      return newSet;
    });
  };

  // Handle verse reference click from TSK
  const handleTSKVerseReference = (reference, rangeInfo) => {
    if (rangeInfo?.isRange) {
      // Parse the link reference (first verse of range)
      const parseRef = (ref) => {
        let match = ref?.match(/^(.+?)\s+(\d+):(\d+)$/);
        if (!match) return null;
        return { bookName: match?.[1], chapter: parseInt(match?.[2]), verse: parseInt(match?.[3]) };
      };
      const parsed = parseRef(reference);
      if (parsed) {
        const isSameChapter =
          parsed?.bookName?.toLowerCase() === book?.toLowerCase() &&
          parsed?.chapter === chapter;
        if (isSameChapter) {
          // Highlight the range in current chapter
          const newHighlighted = new Set(highlightedVerses);
          for (let v = rangeInfo?.rangeStart; v <= rangeInfo?.rangeEnd; v++) {
            newHighlighted?.add(v);
          }
          setHighlightedVerses(newHighlighted);
          setTimeout(() => {
            const element = document.querySelector(`[data-verse-number="${parsed?.verse}"]`);
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
          return;
        }
      }
    }
    if (onVerseReferenceClick) {
      onVerseReferenceClick(reference);
    }
  };

  const handleVerseReferenceClick = (reference) => {
    console.log('Commentary verse reference clicked:', reference);
    // Parse reference and navigate to that verse
    // Example: "John 7:50" or "Matthew 10:28"

    const parseReference = (ref) => {
      let match = ref?.match(/^(\w+)\s+(\d+):(\d+)(?:-(\d+))?/);
      if (match) {
        const [, bookName, chapterNum, startVerse, endVerse] = match;
        return {
          book: bookName?.toLowerCase(),
          chapter: parseInt(chapterNum),
          startVerse: parseInt(startVerse),
          endVerse: endVerse ? parseInt(endVerse) : parseInt(startVerse)
        };
      }
      return null;
    };

    const parsedRef = parseReference(reference);
    if (parsedRef) {
      // In a real application, this would navigate to the referenced verse
      // For now, we'll just highlight it if it's in the current chapter
      if (parsedRef?.book === book && parsedRef?.chapter === chapter) {
        const newHighlighted = new Set(highlightedVerses);
        for (let v = parsedRef?.startVerse; v <= parsedRef?.endVerse; v++) {
          newHighlighted?.add(v);
        }
        setHighlightedVerses(newHighlighted);

        // Scroll to the verse if it exists in current view
        setTimeout(() => {
          const element = document.querySelector(`[data-verse-number="${parsedRef?.startVerse}"]`);
          if (element) {
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        // Navigate to different chapter/book using the parent's navigation handler
        if (onVerseReferenceClick) {
          onVerseReferenceClick(reference);
        }
      }
    }
  };

  const handleVerseReflect = (verse) => {
    if (!user) {
      showWarning('Please sign in to add notes and reflections.', 'Sign In Required');
      navigate('/login', { state: { from: '/bible-reader' } });
      return;
    }
    const verseData = {
      reference: verse?.reference || `${book?.charAt(0)?.toUpperCase() + book?.slice(1)} ${chapter}:${verse?.number}`,
      text: verse?.text,
      version: version?.toUpperCase(),
      book: book,
      chapter: chapter,
      verse: verse?.number
    };

    // Navigate to memorization practice with verse data
    navigate('/memorization-practice', { state: { selectedVerse: verseData } });
  };

  const handleViewReflections = (verse) => {
    const interactionData = verseInteractions?.get(verse?.number);
    const verseForViewing = {
      id: `${book}-${chapter}-${verse?.number}`,
      reference: verse?.reference || `${book?.charAt(0)?.toUpperCase() + book?.slice(1)} ${chapter}:${verse?.number}`,
      text: verse?.text,
      version: version?.toUpperCase(),
      reflections: interactionData?.publicReflections || []
    };

    setSelectedVerseForViewing(verseForViewing);
    setIsViewReflectionsModalOpen(true);
  };

  const handleViewTSK = (verse) => {
    setSelectedVerseForTSK({
      book: book,
      chapter: chapter,
      verse: verse?.number
    });
  };

  const handleSaveReflection = async (reflection) => {
    try {
      if (!user?.id) {
        showError('Please sign in to save reflections');
        return;
      }

      // Transform reflection data for database
      const reflectionData = {
        user_id: user?.id,
        verse_id: null, // Will try to find matching verse in user's library
        insights: reflection?.reflections?.insights || null,
        actions: reflection?.reflections?.actions || null, 
        relationships: reflection?.reflections?.relationship || null,
        illustrations: reflection?.reflections?.illustration || null,
        testimonies: reflection?.reflections?.testimony || null,
        is_private: reflection?.isPrivate ?? true
      };

      // Try to find matching verse in user's library first
      const verseRef = reflection?.verseReference;
      const { data: existingVerse, error: verseError } = await supabase?.from('verses')?.select('id')?.eq('user_id', user?.id)?.eq('reference', verseRef)?.maybeSingle();

      if (verseError) {
        console.warn('Error finding existing verse:', verseError);
      }

      if (existingVerse) {
        // Reflection for existing verse in library
        reflectionData.verse_id = existingVerse?.id;
      } else {
        // Create verse first, then reflection
        const referenceMatch = verseRef?.match(/^(\w+(?:\s+\w+)*)\s+(\d+):(\d+)(?:-(\d+))?$/);
        if (referenceMatch) {
          const [, bookName, chapterNum, startVerse, endVerse] = referenceMatch;
          
          const verseData = {
            user_id: user?.id,
            reference: verseRef,
            text: reflection?.verseText,
            book: bookName,
            chapter: parseInt(chapterNum),
            verse_start: parseInt(startVerse),
            verse_end: endVerse ? parseInt(endVerse) : null,
            bible_version: 'kjv', // Default version for bible reader
            current_phase: 1,
            current_interval: 1.0,
            next_review_date: new Date()?.toISOString()?.split('T')?.[0]
          };

          const { data: newVerse, error: createVerseError } = await supabase?.from('verses')?.insert(verseData)?.select('id')?.single();

          if (createVerseError) {
            throw createVerseError;
          }

          reflectionData.verse_id = newVerse?.id;
        }
      }

      // Save reflection to database
      const { data, error } = await reflectionService?.createReflection(reflectionData);

      if (error) {
        throw error;
      }

      console.log('Reflection successfully saved to database:', data);
      
      // Refresh interaction data to show updated reflection count
      loadVerseInteractions();
      
      showSuccess(`Reflection saved for ${reflection?.verseReference}`, 'Saved');
      
    } catch (error) {
      console.error('Error saving reflection:', error);
      showError(error?.message || 'Failed to save reflection. Please try again.');
    }
  };

  const handleAddToMemorization = (verse) => {
    if (!user) {
      showWarning('Please sign in to create a memorization practice session.', 'Sign In Required');
      navigate('/login', { state: { from: '/bible-reader' } });
      return;
    }
    const verseData = {
      reference: verse?.reference || `${book?.charAt(0)?.toUpperCase() + book?.slice(1)} ${chapter}:${verse?.number}`,
      text: verse?.text,
      version: version?.toUpperCase(),
      book: book,
      chapter: chapter,
      verse: verse?.number
    };

    // Navigate to memorization practice with verse data
    navigate('/memorization-practice', { state: { selectedVerse: verseData } });
  };

  const handleAddToLibrary = (verse) => {
    if (!user) {
      showWarning('Please sign in to add verses to your library.', 'Sign In Required');
      navigate('/login', { state: { from: '/bible-reader' } });
      return;
    }
    // Check if verse is already in library
    const libraryData = userVerseLibrary?.get(verse?.number);
    if (libraryData) {
      showWarning('This verse is already in your library.', 'Already in Library');
      return;
    }
    setSelectedVerseForLibrary(verse?.number);
    setIsAddToLibraryModalOpen(true);
  };

  const handleAddLink = (verse) => {
    // Build a source verse object; include library id if available
    const libraryData = userVerseLibrary?.get(verse?.number);
    setSelectedVerseForLink({
      id: libraryData?.id || null,
      reference: verse?.reference || `${book?.charAt(0)?.toUpperCase() + book?.slice(1)} ${chapter}:${verse?.number}`,
      text: verse?.text,
      book,
      chapter,
      verse: verse?.number,
    });
    setIsAddLinkModalOpen(true);
  };

  // Handle file-folder tab selection for a verse
  const handleVerseTabSelect = (verseNumber, tabName) => {
    setActiveVerseTab((prev) => {
      const current = prev?.[verseNumber];
      // Toggle off if same tab clicked again
      if (current === tabName) {
        return { ...prev, [verseNumber]: null };
      }
      // Fire GA events when opening study feature tabs
      if (tabName === 'egw') {
        trackEGWVerseAction(book, chapter, verseNumber, version);
      } else if (tabName === 'commentaries') {
        trackCommentariesVerseAction(book, chapter, verseNumber, version);
      } else if (tabName === 'tsk') {
        trackTSKVerseAction(book, chapter, verseNumber, version);
      } else if (['sda', 'interlinear', 'reflect', 'memorize']?.includes(tabName)) {
        trackVerseActionOpen(tabName, book, chapter, verseNumber, version);
      }
      return { ...prev, [verseNumber]: tabName };
    });
  };

  // Get interaction data for a verse
  const getVerseInteractionData = (verse) => {
    const interactionData = verseInteractions?.get(verse?.number);
    return {
      reflectionCount: interactionData?.reflectionCount || 0,
      libraryCount: interactionData?.libraryCount || 0,
      hasPublicReflections: (interactionData?.publicReflections?.length || 0) > 0
    };
  };

  // Heatmap color scheme based on reflection popularity - theme-aware amber tints
  const getHeatmapColorScheme = (reflectionCount) => {
    if (reflectionCount === 0) return { bg: '', border: '', text: 'text-muted-foreground' };
    if (reflectionCount <= 2) return {
      bg: '',
      border: 'border-primary/20',
      text: 'text-foreground',
      intensity: 'low',
      bgStyle: 'rgba(245, 158, 11, 0.06)'
    };
    if (reflectionCount <= 5) return {
      bg: '',
      border: 'border-primary/30',
      text: 'text-foreground',
      intensity: 'moderate',
      bgStyle: 'rgba(245, 158, 11, 0.10)'
    };
    if (reflectionCount <= 10) return {
      bg: '',
      border: 'border-primary/40',
      text: 'text-foreground',
      intensity: 'popular',
      bgStyle: 'rgba(245, 158, 11, 0.14)'
    };
    if (reflectionCount <= 20) return {
      bg: '',
      border: 'border-primary/50',
      text: 'text-foreground',
      intensity: 'very popular',
      bgStyle: 'rgba(245, 158, 11, 0.18)'
    };
    return {
      bg: '',
      border: 'border-primary/60',
      text: 'text-foreground',
      intensity: 'extremely popular',
      bgStyle: 'rgba(245, 158, 11, 0.22)'
    };
  };

  if (loading) {
    return (
      <div className={`bg-card rounded-lg border border-border p-4 ${className}`}>
        <div className="mb-3 flex items-center gap-2">
          <div className="h-5 w-32 bg-muted rounded animate-pulse" />
          <div className="h-4 w-16 bg-muted rounded animate-pulse" />
        </div>
        <VerseListSkeleton count={12} />
      </div>
    );

  }

  if (error) {
    return (
      <div className={`bg-card rounded-lg border border-destructive p-8 text-center ${className}`}>
        <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="AlertCircle" size={24} className="text-destructive" />
        </div>
        <h3 className="text-lg font-heading font-semibold text-destructive mb-2">
          Error Loading Chapter
        </h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button
          onClick={() => window.location?.reload()}
          variant="outline"
          size="sm">

          <Icon name="RefreshCw" size={14} className="mr-2" />
          Retry
        </Button>
      </div>);

  }

  if (!book || !chapter) {
    return (
      <div className={`bg-card rounded-lg border border-border p-8 text-center ${className}`}>
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="BookOpen" size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
          Select Book and Chapter
        </h3>
        <p className="text-muted-foreground">
          Please select a book and chapter to read the Bible text.
        </p>
      </div>);

  }

  if (verses?.length === 0) {
    return (
      <div className={`bg-card rounded-lg border border-border p-8 text-center ${className}`}>
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="BookOpen" size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
          Chapter Not Available
        </h3>
        <p className="text-muted-foreground">
          No verses found for {book} {chapter} in the database.
        </p>
      </div>);

  }

  return (
    <>
      <div className={`bg-card rounded-lg border border-border overflow-hidden ${className}`} style={{ '--reader-font-size': getTextSizePx() }}>
        {/* Chapter Header */}
        <div className="border-b border-border px-3 sm:px-4 py-2">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-1.5 sm:gap-2">
            <div>
              <h2 className="font-heading font-bold text-foreground capitalize text-2xl sm:text-3xl flex items-baseline gap-2 sm:gap-3">
                {localizedBookName
                  ? localizedBookName
                  : (book?.charAt(0)?.toUpperCase() + book?.slice(1))
                } {chapter}
                <span className="text-sm sm:text-base font-normal text-muted-foreground">{version?.toUpperCase()}</span>
              </h2>
            </div>
            
            <div className="flex items-center gap-1.5 sm:gap-4 flex-wrap w-full sm:w-auto">
              {/* Strong's Numbers Toggle */}
              <button
                onClick={hasStrongs ? handleToggleStrongsNumbers : undefined}
                title={!hasStrongs ? "Strong's Numbers not available for this version" : showStrongsNumbers ? "Hide Strong's Numbers" : "Show Strong's Numbers"}
                disabled={!hasStrongs}
                className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-md border transition-colors text-xs font-medium ${
                  !hasStrongs
                    ? 'border-border bg-background text-muted-foreground/40 cursor-not-allowed opacity-50'
                    : showStrongsNumbers
                      ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground'
                }`}
                aria-label={!hasStrongs ? "Strong's Numbers not available" : showStrongsNumbers ? "Hide Strong's Numbers" : "Show Strong's Numbers"}
              >
                <Icon
                  name={showStrongsNumbers && hasStrongs ? 'Eye' : 'EyeOff'}
                  size={14}
                />
                <span className="hidden sm:inline">Strong's</span>
              </button>

              {/* Social Engagement Toggle — with highlight count badge */}
              <button
                onClick={handleToggleSocialEngagement}
                title={showSocialEngagement ? 'Hide Community' : 'Show Community'}
                className={`relative flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-md border transition-colors text-xs font-medium ${
                  showSocialEngagement
                    ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground'
                }`}
                aria-label={showSocialEngagement ? 'Hide Community' : 'Show Community'}
              >
                <Icon
                  name={showSocialEngagement ? 'Users' : 'UserX'}
                  size={14}
                />
                <span className="hidden sm:inline">Community</span>
              </button>

              {/* Text Size Control */}
              <div className="flex items-center gap-0 border border-border rounded-md overflow-hidden bg-background">
                <button
                  onClick={() => {
                    const idx = TEXT_SIZES?.findIndex(s => s?.key === textSize);
                    if (idx > 0) setTextSize(TEXT_SIZES?.[idx - 1]?.key);
                  }}
                  title="Decrease text size"
                  disabled={TEXT_SIZES?.findIndex(s => s?.key === textSize) === 0}
                  className="px-2.5 py-1.5 font-medium transition-colors min-w-[28px] min-h-[32px] leading-none text-sm text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  −
                </button>
                <span className="px-2 py-1.5 min-w-[28px] min-h-[32px] leading-none flex items-center justify-center font-medium text-foreground select-none border-x border-border"
                  style={{ fontSize: getTextSizePx() }}>
                  T
                </span>
                <button
                  onClick={() => {
                    const idx = TEXT_SIZES?.findIndex(s => s?.key === textSize);
                    if (idx < TEXT_SIZES?.length - 1) setTextSize(TEXT_SIZES?.[idx + 1]?.key);
                  }}
                  title="Increase text size"
                  disabled={TEXT_SIZES?.findIndex(s => s?.key === textSize) === TEXT_SIZES?.length - 1}
                  className="px-2.5 py-1.5 font-medium transition-colors min-w-[28px] min-h-[32px] leading-none text-sm text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>

              {/* Audio / Study Tools / Fullscreen — injected from parent */}
              {toolbarButtons && (
                <div className="flex items-center gap-1 flex-shrink-0 ml-auto sm:ml-0">
                  {toolbarButtons}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bible Text */}
        <div className="space-y-1 sm:space-y-2 max-h-[600px] overflow-y-auto p-2 sm:p-2.5">
          {verses?.map((verse) => {
            const interactionData = getVerseInteractionData(verse);
            const isSelected = selectedVerse?.number === verse?.number;
            const isHighlighted = highlightedVerses?.has(verse?.number);
            const isSearchHighlighted = highlightVerseNumber === verse?.number;
            const isAudioActive = audioActiveVerse === verse?.number;
            const heatmapColors = getHeatmapColorScheme(interactionData?.reflectionCount);
            const hasCommentary = visibleCommentaries?.has(verse?.number);
            const hasEGWComments = visibleEGWComments?.has(verse?.number);
            const hasTSK = visibleTSK?.has(verse?.number);
            const hasEGWVerseInsights = visibleEGWVerseInsights?.has(verse?.number);
            const hasCommentariesInsights = visibleCommentariesInsights?.has(verse?.number);

            // Check if this verse is in user's library
            const libraryData = userVerseLibrary?.get(verse?.number);
            const isInLibrary = libraryData !== undefined;
            const phaseColor = isInLibrary ? getPhaseColors(libraryData?.phase) : null;

            // Highlight data
            const highlightData = verseHighlights?.get(verse?.number);
            const isHighlightedByUser = !!highlightData;
            const highlightStyle = isHighlightedByUser ? getHighlightStyle(highlightData?.color) : null;
            const isPickerOpen = highlightPickerVerse === verse?.number;
            const highlightColorMeta = isHighlightedByUser ? HIGHLIGHT_COLORS?.find(c => c?.id === highlightData?.color) : null;

            return (
              <div key={verse?.number} id={`verse-${verse?.number}`} data-verse={verse?.number} className="group relative">
                <div
                  className={`flex items-start space-x-3 p-2.5 sm:p-2 rounded-lg transition-all duration-200 ${
                    isAudioActive ? 'border-l-4 border-primary' : isSearchHighlighted ? 'border-2 border-primary shadow-lg': isHighlighted ? 'border border-primary/40' :
                    isHighlightedByUser ? 'border-l-4' :
                    interactionData?.reflectionCount > 0 ? `${heatmapColors?.border} border` : ''
                  }`}
                  style={{
                    backgroundColor: isAudioActive ? 'rgba(245, 158, 11, 0.12)' :
                    isSearchHighlighted ? 'rgba(245, 158, 11, 0.15)' : isHighlighted ? 'rgba(245, 158, 11, 0.10)' :
                    isHighlightedByUser ? highlightStyle?.backgroundColor
                      : interactionData?.reflectionCount > 0 ? heatmapColors?.bgStyle :
                      isInLibrary ? 'rgba(245, 158, 11, 0.08)' :
                      undefined,
                    borderLeftColor: isHighlightedByUser && !isAudioActive && !isSearchHighlighted && !isHighlighted
                      ? highlightStyle?.borderColor
                      : undefined,
                  }}
                  data-verse-number={verse?.number}>

                  {/* Verse Number */}
                  <button
                    onClick={() => {
                      if (onVerseNumberAudioClick) {
                        onVerseNumberAudioClick(verse?.number);
                      }
                      handleVerseNumberClick(verse);
                    }}
                    title={onVerseNumberAudioClick ? 'Click to seek audio to this verse' : undefined}
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border transition-all duration-200 ${
                    isAudioActive ?
                    'bg-primary text-primary-foreground border-primary shadow-sm' :
                    isSelected ?
                    'bg-primary text-primary-foreground border-primary' :
                    isHighlighted ?
                    'bg-warning/20 text-warning border-warning/50' :
                    isInLibrary ?
                    'bg-card/80 text-primary border-primary shadow-sm' :
                    interactionData?.reflectionCount > 0 ?
                    `${heatmapColors?.text} bg-card/70 border-current hover:bg-card/90` :
                    'bg-muted text-muted-foreground border-border hover:border-primary/30 hover:bg-primary/5'
                    }`}

                    >
                    {verse?.number}
                    {isInLibrary &&
                    <div
                      className="absolute -top-1 -right-1 w-3 h-3 rounded-full border border-background shadow-sm bg-primary"
                      title={`In Library — ${getPhaseLabel(libraryData?.phase)}`} />
                    }
                  </button>

                  {/* Verse Content */}
                  <div className="flex-1 min-w-0">
                    <div
                      className={`cursor-pointer transition-all duration-200 rounded-md px-1 -mx-1 ${
                      isSelected 
                        ? 'text-foreground bg-primary/[0.08]' 
                        : 'text-foreground/90 hover:text-foreground hover:bg-primary/5'
                      }`}
                      onClick={() => handleVerseClick(verse)}>

                      <p className="font-medium leading-relaxed transition-all duration-300 py-0.5 sm:py-0" style={getTextStyle()}>
                        {showStrongsNumbers 
                          ? <span className="strongs-lens-active animate-in fade-in duration-300">{renderVerseWithStrongs(verse?.text)}</span>
                          : renderVerseWithoutStrongs(verse?.text)
                        }
                      </p>
                      
                      {/* Library indicator for ranges */}
                      {isInLibrary && libraryData?.isRange &&
                      <div className="mt-1 text-xs text-primary/70 font-medium">
                          Part of {libraryData?.reference} (Phase {libraryData?.phase})
                        </div>
                      }
                    </div>

                    {/* Highlight indicator row — shown when verse is highlighted */}
                    {isHighlightedByUser && (
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          data-highlight-picker
                          onClick={(e) => {
                            e?.stopPropagation();
                            setHighlightPickerVerse(prev => prev === verse?.number ? null : verse?.number);
                          }}
                          title="Change or remove highlight"
                          className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-all hover:opacity-90 hover:shadow-sm font-medium"
                          style={{
                            backgroundColor: highlightStyle?.backgroundColor,
                            borderColor: highlightStyle?.borderColor,
                            color: highlightColorMeta?.text,
                          }}
                        >
                          <Icon name="Highlighter" size={11} />
                          <span>{highlightColorMeta?.label || 'Highlight'}</span>
                        </button>

                        {/* Color picker popover */}
                        {isPickerOpen && (
                          <div
                            data-highlight-picker
                            className="flex items-center gap-2 flex-wrap mt-2 mb-1 bg-popover border border-border rounded-xl px-3 py-2.5 shadow-lg"
                          >
                            <span className="text-xs text-muted-foreground font-medium w-full mb-1">Choose highlight color:</span>
                            {HIGHLIGHT_COLORS?.map(c => (
                              <button
                                key={c?.id}
                                onClick={(e) => { e?.stopPropagation(); handleHighlightColor(verse, c?.id); }}
                                title={c?.label}
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border-2 transition-all hover:scale-105 text-xs font-medium ${
                                  highlightData?.color === c?.id ? 'border-foreground shadow-md scale-105' : 'border-transparent hover:border-current'
                                }`}
                                style={{ backgroundColor: c?.bg, outline: `2px solid ${c?.border}`, color: c?.text }}
                              >
                                {highlightData?.color === c?.id && <Icon name="Check" size={11} />}
                                {c?.label}
                              </button>
                            ))}
                            {isHighlightedByUser && (
                              <>
                                <div className="w-full h-px bg-border my-1" />
                                <button
                                  onClick={() => handleRemoveHighlight(verse)}
                                  title="Remove highlight"
                                  className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors"
                                >
                                  <Icon name="X" size={11} />
                                  Remove highlight
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Interaction indicators — only shown when showSocialEngagement is true */}
                    {showSocialEngagement && (
                    <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                      {/* Left side - Interaction indicators */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Reflections Count Button */}
                        {interactionData?.reflectionCount > 0 &&
                        <button
                          onClick={() => handleViewReflections(verse)}
                          className={`flex items-center space-x-1 text-sm px-3 py-1.5 rounded-md transition-colors duration-200 hover:bg-muted border text-foreground ${heatmapColors?.border || 'border-border'}`}
                          title="View Public Reflections">
                            <Icon name="MessageSquare" size={14} />
                            <span className="font-medium">
                              {interactionData?.reflectionCount} 
                            </span>
                          </button>
                        }

                        {/* User Avatars - Show users who have interacted with this verse */}
                        {verseEngagementUsers?.get(verse?.number) && verseEngagementUsers?.get(verse?.number)?.users?.length > 0 &&
                        <div className="flex items-center">
                            <VerseEngagement
                              users={verseEngagementUsers?.get(verse?.number)?.users || []}
                              maxDisplay={4}
                            />
                          </div>
                        }

                        {/* Library status indicator — icon only with hover tooltip */}
                        {isInLibrary && (
                          <div className="relative group/library">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary cursor-default">
                              <Icon name="BookMarked" size={14} />
                            </div>
                            {/* Hover tooltip */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1.5 bg-popover text-popover-foreground text-xs rounded whitespace-nowrap opacity-0 invisible group-hover/library:opacity-100 group-hover/library:visible transition-all duration-200 pointer-events-none z-[100]">
                              <div className="font-medium text-[11px] text-muted-foreground">Phase {libraryData?.phase}</div>
                              <div className="font-semibold">{libraryData?.reference || `${book?.charAt(0)?.toUpperCase() + book?.slice(1)} ${chapter}:${verse?.number}`}</div>
                              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-popover" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {/* Verse Actions — file-folder tab design */}
                    {showVerseActions === verse?.number && (
                      <VerseActionsPanel
                        verse={verse}
                        book={book}
                        chapter={chapter}
                        version={version}
                        activeVerseTab={activeVerseTab}
                        isInLibrary={isInLibrary}
                        isHighlightedByUser={isHighlightedByUser}
                        highlightData={highlightData}
                        isPickerOpen={isPickerOpen}
                        getTextSizePx={getTextSizePx}
                        handleVerseTabSelect={handleVerseTabSelect}
                        handleAddToMemorization={handleAddToMemorization}
                        handleAddToLibrary={handleAddToLibrary}
                        handleAddLink={handleAddLink}
                        handleHighlightColor={handleHighlightColor}
                        handleRemoveHighlight={handleRemoveHighlight}
                        setHighlightPickerVerse={setHighlightPickerVerse}
                        handleVerseReferenceClick={handleVerseReferenceClick}
                        handleTSKVerseReference={handleTSKVerseReference}
                        user={user}
                        navigate={navigate}
                        showWarning={showWarning}
                        HIGHLIGHT_COLORS={HIGHLIGHT_COLORS}
                      />
                    )}
                  </div>
                </div>
              </div>
            );


          })}
        </div>
      </div>
      {/* Strong's Number Card */}
      <StrongsNumberCard
        definition={activeStrongsCard ? strongsDefinitions?.[activeStrongsCard] : null}
        isVisible={!!activeStrongsCard}
        onClose={closeStrongsCard}
        position={cardPosition} />
      {/* Verse Reflection Modal — kept for external callers; selectedVerseForReflection must be set to open */}
      {selectedVerseForReflection && (
        <VerseReflectionModal
          verse={selectedVerseForReflection}
          isOpen={isReflectionModalOpen}
          onClose={() => {
            setIsReflectionModalOpen(false);
            setSelectedVerseForReflection(null);
          }}
          onSave={handleSaveReflection} />
      )}
      {/* Verse Reflections View Modal */}
      <VerseReflectionsViewModal
        verse={selectedVerseForViewing}
        isOpen={isViewReflectionsModalOpen}
        onClose={() => {
          setIsViewReflectionsModalOpen(false);
          setSelectedVerseForViewing(null);
        }}
        currentUser={currentUser} />
      {/* Add To Library Modal */}
      <AddToLibraryModal
        isOpen={isAddToLibraryModalOpen}
        onClose={() => {
          setIsAddToLibraryModalOpen(false);
          setSelectedVerseForLibrary(null);
          // Refresh interactions and library after adding to library
          loadVerseInteractions();
          loadUserVerseLibrary();
        }}
        book={book}
        chapter={chapter}
        version={version}
        verses={verses}
        selectedVerseNumber={selectedVerseForLibrary} />
      {/* Add Link Modal */}
      <AddLinkModal
        isOpen={isAddLinkModalOpen}
        onClose={() => {
          setIsAddLinkModalOpen(false);
          setSelectedVerseForLink(null);
        }}
        sourceVerse={selectedVerseForLink}
        book={book}
        chapter={chapter}
        version={version}
      />
    </>
  );


};

export default BibleTextDisplay;